import Axios from 'axios'
import groupBy from 'lodash.groupby'
import omit from 'lodash.omit'
import sortBy from 'lodash.sortby'
import startCase from 'lodash.startcase'
import queryString from 'query-string'
import urlJoin from 'url-join'

import { BASE_URL, CREATE_PROJECT_BASE_URL } from '../config'

import axios from './../../function/axiosInstance'
import extract from './../../function/extract'

const url = `${BASE_URL}/utils`

type ReturnType = Promise<{
  status: 'ok' | 'error'
  data: unknown
  error?: string
}>

export const getUtilityEnums = async (): ReturnType => {
  try {
    const response = await axios.get(`/utils`)
    return {
      status: 'ok',
      data: response.data
    }
  } catch (error) {
    return extract.error(error)
  }
}

export const getAssignmentInfoFromToken = async ({
  token
}: {
  token: string | null
}): Promise<GetTokenAssignmentInfo> => {
  if (!token) {
    throw new Error('Assignment id is required.')
  }

  const headers = {
    'Content-Type': 'application/json'
  }

  try {
    const response = await fetch(`${url}/decode`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token })
    })
    const result = await response.json()
    if (response.ok) {
      return {
        status: 'ok',
        data: result,
        error: undefined
      }
    }
    throw new Error(result.description)
  } catch (error) {
    return {
      status: 'error',
      data: undefined,
      error: error.message
    }
  }
}

export const getFeedbackInputs = async (): Promise<ApiResponse> => {
  const ASSET_BASE_URL = 'https://assets.peppercontent.io/'
  try {
    const response = await fetch(
      urlJoin(ASSET_BASE_URL, '/utils/feedback_inputs.json')
    )
    const result = await response.json()
    return {
      status: 'ok',
      data: Array.isArray(result) ? result : [],
      error: undefined
    }
  } catch (error) {
    return {
      status: 'error',
      data: [],
      error: error.message ?? 'Something went wrong'
    }
  }
}

const serviceIds = {
  writing: 1,
  design: 2,
  language_service: 3,
  others: 4
}

const filterVerticals = (
  verticals: VerticalObjectType[],
  isStandard = false
) => {
  return verticals.filter(vertical => {
    if (!Array.isArray(vertical.display)) return false
    if (vertical.display.length === 0) return false

    return vertical.display.some(_display => {
      if (isStandard) {
        return ['client_sme'].includes(_display)
      }

      return ['client_enterprise'].includes(_display)
    })
  })
}

const transformVerticals = (verticals: VerticalObjectType[]) => {
  return verticals.map(vertical => {
    return {
      id: vertical.id,
      isStandard: vertical.online_purchase_available,
      isRestricted: Boolean(vertical.unrestricted_flag),
      title: vertical.display_name,
      icon_set: vertical.thumbnails,
      description: vertical.description,
      key: vertical.key as string,
      display_order: vertical.display_order
    }
  })
}

const sortVerticalsByDisplayOrder = (verticals: VerticalObjectType[]) => {
  return sortBy(verticals, ['display_order'])
}

interface GetPreDataJSONArgs {
  isStandard?: boolean
}

export const getPreDataJSON = async ({
  isStandard = false
}: GetPreDataJSONArgs): Promise<GetPreDataFields> => {
  try {
    const response = await axios.get('/utils')
    const { verticals } = response.data
    const subCategories = response.data.sub_categories as SubCategories[]
    const subCategoriesMap = new Map()
    subCategories.forEach(item => {
      const key = item.id
      subCategoriesMap.set(key, item)
    })

    const group = groupBy(verticals, 'sub_category_id')

    const result: PreDataProps = {
      data: [],
      typeContentSelection: {
        header: 'What do you want to create today?',
        body: {
          type: 'tabs',
          subType: 'listShownSideBySide',
          search: {
            id: 'projectTypeSearch',
            placeHolder: 'Search for a Service'
          },
          id: 'projectTypes',
          options: Object.keys(omit(group, ['null'])).map(item => {
            const filteredVerticals = filterVerticals(group[item], isStandard)
            const sortedVerticals =
              sortVerticalsByDisplayOrder(filteredVerticals)
            const options = transformVerticals(sortedVerticals)

            return {
              id: Number.parseInt(item),
              name: startCase(
                subCategoriesMap.get(Number.parseInt(item)).title || ''
              ),
              options
            }
          })
        }
      }
    }

    if (response.status !== 200) {
      throw new Error('Error occurred while fetching data')
    }

    return {
      status: 'ok',
      data: result,
      error: undefined
    }
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const getCreateProjectJSON = async (
  verticalId: number,
  version = 'latest'
): Promise<any> => {
  try {
    const _url = queryString.stringifyUrl(
      {
        url: CREATE_PROJECT_BASE_URL,
        query: { key: `vertical-${verticalId}`, version }
      },
      { skipNull: true, skipEmptyString: true }
    )

    const response = await Axios.get(_url)

    const extracted = extract.data(response)
    return extracted
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const getVerticalMappingJSON = async (): Promise<
  ApiResponseV2<{ response: IVerticalMapping }>
> => {
  const currentDate = new Date()
  const msFromNearest15 =
    (currentDate.getSeconds() * 1000 + currentDate.getMilliseconds()) % 15000
  const last15Timestamp = currentDate.getTime() - msFromNearest15
  try {
    const _url = `https://assets.peppercontent.io/utils/create-project-v2/vertical-match.json?${last15Timestamp}`
    const response = await Axios.get(_url)
    return extract.data(response)
  } catch (error) {
    return {
      status: 'error',
      data: null,
      error: error.message
    }
  }
}

export const getSeoCountriesJSON = async (): Promise<
  ApiResponseV2<{
    response: Record<SEOCountries, SEOCountryData>
  }>
> => {
  try {
    const _url = `https://assets.peppercontent.io/utils/seo-countries.json`
    const response = await Axios.get(_url)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}

export const getPhoneExtensionsJSON = async (): Promise<
  ApiResponseV2<{
    response: {
      value: string
      label: string
      flag: string
      extension: string
      isRecommended: boolean
    }[]
  }>
> => {
  try {
    const _url = `https://assets.peppercontent.io/utils/country_phone_ext.json`
    const response = await Axios.get(_url)
    return extract.data(response)
  } catch (error) {
    return extract.error(error)
  }
}
